import {DriveLayout, StationLayout} from '@domain/kiln-monitoring'
import {KilnShellCone} from '@domain/kiln-monitoring/src/types/kmsKilnInfo.types'
import {Stack} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {roundTo, getKilnPartDimensions, getKilnShellDimensions} from '../../../../helpers'
import {useKilnConfig} from '../../../../providers'
import {KilnConfigActionType} from '../../../../types'
import {KilnConfigTextField} from '../KilnConfigTextField'

interface KilnPartDimensionsInputsProps {
  selectedKilnPart: StationLayout | DriveLayout | KilnShellCone
  showDiameter?: boolean
}

export const KilnPartDimensionsInputs: React.FC<KilnPartDimensionsInputsProps> = ({
  selectedKilnPart,
  showDiameter = false
}) => {
  const {t} = useTranslation()

  const {kilnConfiguration, kilnLayout, dispatch} = useKilnConfig()

  const {kilnLengthInMeters, metersInPixel} = getKilnShellDimensions(kilnConfiguration, kilnLayout)

  const {
    distanceFrom0: {distanceFrom0InMeters, distanceFrom0InPixels},
    width: {widthInMeters, isWidthEmpty},
    diameter: {diameterInMeters, isDiameterEmpty}
  } = getKilnPartDimensions(selectedKilnPart)

  const isDistanceFrom0Disabled = isWidthEmpty || (showDiameter && isDiameterEmpty)

  return (
    <Stack direction="row" spacing={1.5}>
      <KilnConfigTextField
        type="number"
        label={t('kilnConfig.distanceFrom0')}
        value={distanceFrom0InMeters ?? ''}
        inputProps={{min: 0}}
        helperText={
          isDistanceFrom0Disabled
            ? t(
                showDiameter
                  ? 'kilnConfig.distanceFrom0HelperText2'
                  : 'kilnConfig.distanceFrom0HelperText'
              )
            : ''
        }
        disabled={isDistanceFrom0Disabled}
        onChange={(event) => {
          if (widthInMeters === undefined) return

          const newXPositionInMeters = roundTo(Number(event.target.value))
          let newXPositionInPixels = newXPositionInMeters / metersInPixel

          const isViolatingKilnLength = newXPositionInMeters + widthInMeters > kilnLengthInMeters

          if (isViolatingKilnLength) {
            newXPositionInPixels = (kilnLengthInMeters - widthInMeters) / metersInPixel
          }

          const deltaX = newXPositionInPixels - distanceFrom0InPixels

          if (deltaX === 0) return

          dispatch({
            type: KilnConfigActionType.MOVE_X,
            payload: {id: selectedKilnPart.id, deltaX}
          })
        }}
        data-test-id="kiln-part-distance-from-0-input"
      />
      <KilnConfigTextField
        type="number"
        label={t('kilnConfig.width')}
        value={widthInMeters ?? ''}
        inputProps={{min: 0}}
        onChange={(event) => {
          const newWidthInMeters = roundTo(Number(event.target.value))

          const isViolatingKilnLength =
            newWidthInMeters + (distanceFrom0InMeters ?? 0) > kilnLengthInMeters
          if (isViolatingKilnLength) return

          dispatch({
            type: KilnConfigActionType.SET_KILN_PART_WIDTH,
            payload: {id: selectedKilnPart.id, width: newWidthInMeters}
          })
        }}
        data-test-id="kiln-part-width-input"
      />
      {showDiameter && (
        <KilnConfigTextField
          type="number"
          label={t('kilnConfig.diameter')}
          value={diameterInMeters ?? ''}
          inputProps={{min: 0}}
          onChange={(event) => {
            const newDiameterInMeters = roundTo(Number(event.target.value))
            dispatch({
              type: KilnConfigActionType.SET_KILN_PART_DIAMETER,
              payload: {id: selectedKilnPart.id, diameter: newDiameterInMeters}
            })
          }}
          data-test-id="kiln-part-diameter-input"
        />
      )}
    </Stack>
  )
}
